import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Activity, 
  Zap, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  Search,
  Filter,
  RefreshCw,
  Download,
  Upload,
  Settings,
  Eye,
  Code,
  Server,
  Cpu,
  BarChart3,
  FileText,
  Shield,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';
import esgRawIntelligenceService from '../../../services/esg/esgRawIntelligenceService';

/**
 * ESG Raw Intelligence Component
 * 
 * Comprehensive raw data processing and intelligence platform for ESG data analytics.
 * Provides real-time data ingestion monitoring, transformation pipeline management,
 * intelligent analysis of raw data streams, and advanced data exploration capabilities.
 */
export default function ESGRawIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ingestion');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const unsubscribe = esgRawIntelligenceService.subscribe((newData) => {
      setData(newData);
      setLoading(false);
    });

    // Initial data load
    setData({
      dataIngestion: esgRawIntelligenceService.getDataIngestion(),
      dataProcessing: esgRawIntelligenceService.getDataProcessing(),
      intelligenceAnalysis: esgRawIntelligenceService.getIntelligenceAnalysis(),
      dataQuality: esgRawIntelligenceService.getDataQuality(),
      dataExploration: esgRawIntelligenceService.getDataExploration(),
      dataLineage: esgRawIntelligenceService.getDataLineage(),
      systemMonitoring: esgRawIntelligenceService.getSystemMonitoring(),
      mlIntelligence: esgRawIntelligenceService.getMLIntelligence(),
      dataIntegration: esgRawIntelligenceService.getDataIntegration()
    });
    setLoading(false);

    return unsubscribe;
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate data refresh
    setTimeout(() => {
      setRefreshing(false);
    }, 2000);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      const results = esgRawIntelligenceService.searchRawData(query, { type: filterType === 'all' ? null : filterType });
      console.log('Raw data search results:', results);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'text-green-600 bg-green-50 border-green-200',
      'Configuring': 'text-blue-600 bg-blue-50 border-blue-200',
      'Excellent': 'text-green-600 bg-green-50 border-green-200',
      'Good': 'text-emerald-600 bg-emerald-50 border-emerald-200',
      'Fair': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Poor': 'text-red-600 bg-red-50 border-red-200',
      'Investigating': 'text-orange-600 bg-orange-50 border-orange-200',
      'Resolved': 'text-gray-600 bg-gray-50 border-gray-200',
      'Acknowledged': 'text-blue-600 bg-blue-50 border-blue-200',
      'Success': 'text-green-600 bg-green-50 border-green-200'
    };
    return colors[status] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'High': 'text-red-600 bg-red-50 border-red-200',
      'Medium': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Low': 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors[severity] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading ESG raw intelligence data...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'ingestion', label: 'Data Ingestion', icon: <Database className="w-5 h-5" /> },
    { id: 'processing', label: 'Data Processing', icon: <Cpu className="w-5 h-5" /> },
    { id: 'intelligence', label: 'Intelligence Analysis', icon: <Zap className="w-5 h-5" /> },
    { id: 'quality', label: 'Data Quality', icon: <Shield className="w-5 h-5" /> },
    { id: 'exploration', label: 'Data Exploration', icon: <Search className="w-5 h-5" /> },
    { id: 'lineage', label: 'Data Lineage', icon: <FileText className="w-5 h-5" /> },
    { id: 'monitoring', label: 'System Monitoring', icon: <Activity className="w-5 h-5" /> },
    { id: 'ml', label: 'ML Intelligence', icon: <BarChart3 className="w-5 h-5" /> }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ESG Raw Intelligence</h1>
            <p className="text-gray-600 mt-1">Raw data processing and intelligence platform for comprehensive ESG analytics</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Download className="w-4 h-4" />
              Export
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              <Upload className="w-4 h-4" />
              Import Data
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search raw data, patterns, insights..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="environmental">Environmental</option>
              <option value="social">Social</option>
              <option value="governance">Governance</option>
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        {data?.dataIngestion && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Ingestion Points</p>
                  <p className="text-xl font-bold text-blue-900">{data.dataIngestion.summary.totalIngestionPoints}</p>
                </div>
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-blue-700 text-xs mt-1">{data.dataIngestion.summary.activeStreams} active streams</p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Data Velocity</p>
                  <p className="text-xl font-bold text-green-900">{data.dataIngestion.summary.dataVelocity}</p>
                </div>
                <Activity className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-green-700 text-xs mt-1">{data.dataIngestion.summary.dataVolume} daily</p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium">System Health</p>
                  <p className="text-xl font-bold text-purple-900">{data.dataIngestion.summary.ingestionHealth}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-purple-700 text-xs mt-1">All systems operational</p>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Processing Rate</p>
                  <p className="text-xl font-bold text-orange-900">{formatNumber(data.dataProcessing?.stages?.[0]?.throughput || 0)}/sec</p>
                </div>
                <Cpu className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-orange-700 text-xs mt-1">Real-time processing</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Data Ingestion Tab */}
          {activeTab === 'ingestion' && data?.dataIngestion && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Data Ingestion Sources</h3>
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Add Source
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {data.dataIngestion.sources.map((source) => (
                  <div key={source.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">{source.name}</h4>
                        <p className="text-sm text-gray-600">{source.type}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(source.status)}`}>
                        {source.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-600">Records/sec:</span>
                        <span className="font-medium ml-2">{formatNumber(source.recordsPerSecond)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Quality:</span>
                        <span className="font-medium ml-2">{source.qualityScore}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Latency:</span>
                        <span className="font-medium ml-2">{source.latency}ms</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Error Rate:</span>
                        <span className="font-medium ml-2">{source.errorRate}%</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">Data Types:</div>
                      <div className="flex flex-wrap gap-1">
                        {source.dataTypes.map((type) => (
                          <span key={type} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${source.status === 'Active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-gray-600">Live Status</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <Settings className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Processing Tab */}
          {activeTab === 'processing' && data?.dataProcessing && (
            <div className="space-y-6">
              {/* Processing Stages */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Pipeline</h3>
                <div className="space-y-4">
                  {data.dataProcessing.stages.map((stage, index) => (
                    <div key={stage.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 font-bold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{stage.name}</h4>
                            <p className="text-sm text-gray-600">Queue: {stage.queueDepth} items</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(stage.status)}`}>
                          {stage.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Throughput</div>
                          <div className="font-bold">{formatNumber(stage.throughput)}/sec</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Processed Today</div>
                          <div className="font-bold">{formatNumber(stage.processedToday)}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Avg Time</div>
                          <div className="font-bold">{stage.avgProcessingTime}s</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Success Rate</div>
                          <div className="font-bold">{stage.successRate}%</div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${stage.successRate >= 95 ? 'bg-green-500' : stage.successRate >= 85 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${stage.successRate}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Transformation Rules */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Transformation Rules</h3>
                <div className="space-y-3">
                  {data.dataProcessing.transformationRules.map((rule) => (
                    <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{rule.name}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(rule.status)}`}>
                          {rule.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Type:</div>
                          <div className="font-medium">{rule.type}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Records Processed:</div>
                          <div className="font-medium">{formatNumber(rule.processedRecords)}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Error Rate:</div>
                          <div className="font-medium">{rule.errorRate}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Applicable Types:</div>
                          <div className="font-medium">{rule.applicableTypes.join(', ')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Intelligence Analysis Tab */}
          {activeTab === 'intelligence' && data?.intelligenceAnalysis && (
            <div className="space-y-6">
              {/* Detected Patterns */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Detected Patterns</h3>
                <div className="space-y-4">
                  {data.intelligenceAnalysis.patterns.map((pattern) => (
                    <div key={pattern.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{pattern.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{pattern.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(pattern.severity)}`}>
                            {pattern.severity}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(pattern.status)}`}>
                            {pattern.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                        <div>
                          <div className="text-gray-600">Type:</div>
                          <div className="font-medium">{pattern.type}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Confidence:</div>
                          <div className="font-medium">{pattern.confidence}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Detected:</div>
                          <div className="font-medium">{new Date(pattern.detectedAt).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Sources:</div>
                          <div className="font-medium">{pattern.affectedSources.length}</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {pattern.affectedSources.map((source) => (
                          <span key={source} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI-Generated Insights */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI-Generated Insights</h3>
                <div className="space-y-4">
                  {data.intelligenceAnalysis.insights.map((insight) => (
                    <div key={insight.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(insight.impact)}`}>
                            {insight.impact} Impact
                          </span>
                          <span className="text-xs text-gray-600">{insight.confidence}% confidence</span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm text-gray-600 mb-2">Raw Data Sources:</div>
                        <div className="flex flex-wrap gap-1">
                          {insight.rawDataSources.map((source) => (
                            <span key={source} className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                              {source}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium text-gray-900 mb-2">Recommendations:</div>
                        <ul className="space-y-1">
                          {insight.recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Data Quality Tab */}
          {activeTab === 'quality' && data?.dataQuality && (
            <div className="space-y-6">
              {/* Overall Quality Metrics */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Data Quality</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.entries(data.dataQuality.overall).map(([metric, value]) => (
                    <div key={metric} className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{value}%</div>
                      <div className="text-sm text-gray-600 capitalize">{metric}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className={`h-2 rounded-full ${value >= 95 ? 'bg-green-500' : value >= 85 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${value}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quality by Source */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality by Source</h3>
                <div className="space-y-4">
                  {data.dataQuality.bySource.map((source) => (
                    <div key={source.source} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{source.source}</h4>
                        <span className="text-lg font-bold text-gray-900">
                          {((source.completeness + source.accuracy + source.consistency + source.timeliness + source.validity + source.uniqueness) / 6).toFixed(1)}%
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm mb-4">
                        <div>
                          <div className="text-gray-600">Completeness</div>
                          <div className="font-medium">{source.completeness}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Accuracy</div>
                          <div className="font-medium">{source.accuracy}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Consistency</div>
                          <div className="font-medium">{source.consistency}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Timeliness</div>
                          <div className="font-medium">{source.timeliness}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Validity</div>
                          <div className="font-medium">{source.validity}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Uniqueness</div>
                          <div className="font-medium">{source.uniqueness}%</div>
                        </div>
                      </div>

                      {source.issues && source.issues.length > 0 && (
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-2">Known Issues:</div>
                          <ul className="space-y-1">
                            {source.issues.map((issue, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm text-red-600">
                                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                {issue}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Anomalies */}
              {data.dataQuality.anomalies && data.dataQuality.anomalies.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Anomalies</h3>
                  <div className="space-y-3">
                    {data.dataQuality.anomalies.map((anomaly) => (
                      <div key={anomaly.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{anomaly.type}</h4>
                            <p className="text-sm text-gray-600 mt-1">{anomaly.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(anomaly.severity)}`}>
                              {anomaly.severity}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(anomaly.status)}`}>
                              {anomaly.status}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <span>Source: {anomaly.source}</span>
                          <span>Detected: {new Date(anomaly.detectedAt).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Data Exploration Tab */}
          {activeTab === 'exploration' && data?.dataExploration && (
            <div className="space-y-6">
              {/* Recent Queries */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Queries</h3>
                <div className="space-y-3">
                  {data.dataExploration.recentQueries.map((query) => (
                    <div key={query.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Code className="w-4 h-4 text-gray-600" />
                            <span className="text-sm text-gray-600">Executed by {query.executedBy}</span>
                            <span className="text-sm text-gray-500">•</span>
                            <span className="text-sm text-gray-500">{new Date(query.executedAt).toLocaleString()}</span>
                          </div>
                          <div className="bg-gray-100 rounded-lg p-3 mb-2">
                            <code className="text-sm text-gray-800">{query.query}</code>
                          </div>
                          <p className="text-sm text-gray-600">{query.purpose}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Records Returned:</span>
                          <span className="font-medium ml-2">{formatNumber(query.recordsReturned)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Execution Time:</span>
                          <span className="font-medium ml-2">{query.executionTime}s</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Popular Datasets */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Datasets</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.dataExploration.popularDatasets.map((dataset) => (
                    <div key={dataset.name} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">{dataset.name}</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Access Count:</span>
                          <span className="font-medium">{dataset.accessCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Avg Query Time:</span>
                          <span className="font-medium">{dataset.averageQueryTime}s</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last Accessed:</span>
                          <span className="font-medium">{new Date(dataset.lastAccessed).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <div className="text-sm text-gray-600 mb-2">Top Users:</div>
                        <div className="flex flex-wrap gap-1">
                          {dataset.topUsers.map((user) => (
                            <span key={user} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                              {user}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Data Lineage Tab */}
          {activeTab === 'lineage' && data?.dataLineage && (
            <div className="space-y-6">
              {/* Data Flows */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tracked Data Flows</h3>
                <div className="space-y-4">
                  {data.dataLineage.trackedDataflows.map((flow) => (
                    <div key={flow.id} className="border border-gray-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">{flow.name}</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2">Source</h5>
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm">
                            {flow.source}
                          </span>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2">Transformations</h5>
                          <div className="space-y-1">
                            {flow.transformations.map((transform, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                <span className="text-sm text-gray-700">{transform}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2">Destinations</h5>
                          <div className="space-y-1">
                            {flow.destinations.map((dest, index) => (
                              <span key={index} className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-sm mr-1 mb-1">
                                {dest}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-200 text-sm">
                        <div>
                          <span className="text-gray-600">Records Processed:</span>
                          <span className="font-medium ml-2">{formatNumber(flow.recordsProcessed)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Data Quality:</span>
                          <span className="font-medium ml-2">{flow.dataQuality}%</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Last Tracked:</span>
                          <span className="font-medium ml-2">{new Date(flow.lastTracked).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Audit Trail */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Audit Trail</h3>
                <div className="space-y-2">
                  {data.dataLineage.auditTrail.map((event) => (
                    <div key={event.id} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg">
                      <div className="flex-shrink-0">
                        <CheckCircle className={`w-5 h-5 ${event.status === 'Success' ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{event.action}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-600">{event.user}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-500">{new Date(event.timestamp).toLocaleString()}</span>
                        </div>
                        <div className="text-sm text-gray-600">{event.details}</div>
                        <div className="text-xs text-gray-500">Resource: {event.resource} • IP: {event.ipAddress}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* System Monitoring Tab */}
          {activeTab === 'monitoring' && data?.systemMonitoring && (
            <div className="space-y-6">
              {/* Performance Metrics */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">System Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{data.systemMonitoring.performance.dataProcessingLatency}s</div>
                    <div className="text-sm text-gray-600">Avg Latency</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{formatNumber(data.systemMonitoring.performance.ingestionThroughput)}/s</div>
                    <div className="text-sm text-gray-600">Throughput</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{data.systemMonitoring.performance.storageUtilization}%</div>
                    <div className="text-sm text-gray-600">Storage</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{data.systemMonitoring.performance.queryResponseTime}s</div>
                    <div className="text-sm text-gray-600">Query Time</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{data.systemMonitoring.performance.systemUptime}%</div>
                    <div className="text-sm text-gray-600">Uptime</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{data.systemMonitoring.performance.errorRate}%</div>
                    <div className="text-sm text-gray-600">Error Rate</div>
                  </div>
                </div>
              </div>

              {/* Resource Usage */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(data.systemMonitoring.resources).map(([resource, value]) => (
                    <div key={resource} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 capitalize">{resource.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-lg font-bold text-gray-900">{value}{resource.includes('Utilization') || resource.includes('Usage') ? '%' : ''}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${value >= 90 ? 'bg-red-500' : value >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(100, typeof value === 'number' ? value : 0)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* System Alerts */}
              {data.systemMonitoring.alerts && data.systemMonitoring.alerts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">System Alerts</h3>
                  <div className="space-y-3">
                    {data.systemMonitoring.alerts.map((alert) => (
                      <div key={alert.id} className={`border rounded-lg p-4 ${
                        alert.severity === 'High' ? 'border-red-200 bg-red-50' :
                        alert.severity === 'Medium' ? 'border-yellow-200 bg-yellow-50' :
                        'border-blue-200 bg-blue-50'
                      }`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{alert.type}</h4>
                            <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(alert.severity)}`}>
                              {alert.severity}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(alert.status)}`}>
                              {alert.status}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex flex-wrap gap-1">
                            {alert.affectedSystems.map((system) => (
                              <span key={system} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                {system}
                              </span>
                            ))}
                          </div>
                          <span className="text-gray-500">{new Date(alert.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ML Intelligence Tab */}
          {activeTab === 'ml' && data?.mlIntelligence && (
            <div className="space-y-6">
              {/* ML Models */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Machine Learning Models</h3>
                <div className="space-y-4">
                  {data.mlIntelligence.models.map((model) => (
                    <div key={model.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-900">{model.name}</h4>
                          <p className="text-sm text-gray-600">{model.type}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(model.status)}`}>
                          {model.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Accuracy:</div>
                          <div className="font-bold">{model.accuracy}%</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Training Data:</div>
                          <div className="font-bold">{formatNumber(model.trainingDataPoints)}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Predictions Today:</div>
                          <div className="font-bold">{model.predictionsToday}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Avg Confidence:</div>
                          <div className="font-bold">{model.avgConfidence}%</div>
                        </div>
                      </div>
                      
                      <div className="mt-4 text-sm text-gray-600">
                        Last Trained: {new Date(model.lastTrained).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Automated Insights */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI-Generated Insights</h3>
                <div className="space-y-4">
                  {data.mlIntelligence.automatedInsights.map((insight) => (
                    <div key={insight.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{insight.prediction}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(insight.impact)}`}>
                            {insight.impact} Impact
                          </span>
                          <span className="text-xs text-gray-600">{insight.confidence}% confidence</span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm text-gray-600 mb-1">Data Source: {insight.dataSource}</div>
                        <div className="text-sm text-gray-500">Generated: {new Date(insight.generatedAt).toLocaleString()}</div>
                      </div>

                      <div>
                        <div className="text-sm font-medium text-gray-900 mb-2">Recommended Actions:</div>
                        <ul className="space-y-1">
                          {insight.actionItems.map((action, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
